import { parseISO } from "date-fns";
import type { TimeValue } from "../types";

// ============================================================================
// Date Utilities
// ============================================================================

/**
 * Formats an ISO date string to locale-specific display format
 * @param isoString - ISO 8601 date string
 * @returns Formatted date string in user's locale
 */
export function formatIsoDate(isoString: string): string {
  if (!isoString) return "";
  const date = parseISO(isoString);
  const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
  return new Intl.DateTimeFormat(userLocale).format(date);
}

/**
 * Formats an ISO date string for HTML date input (YYYY-MM-DD)
 * @param isoString - ISO 8601 date string
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForHtmlInput(isoString: string): string {
  if (!isoString) return "";
  return isoString.split("T")[0] ?? "";
}

/**
 * Converts HTML date input value to ISO string
 * Creates date at noon UTC to avoid timezone shifts
 * @param dateValue - Date string in YYYY-MM-DD format
 * @returns ISO 8601 date string
 */
export function htmlDateToIso(dateValue: string): string {
  if (!dateValue) return "";
  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return date.toISOString();
}

// ============================================================================
// Time Utilities
// ============================================================================

/**
 * Formats a TimeValue to locale-specific display format
 * @param timeValue - Time value with hour and minute
 * @param is24Hour - Whether to use 24-hour format
 * @returns Formatted time string in user's locale
 */
export function formatTimeValue(timeValue: TimeValue | undefined, is24Hour = true): string {
  if (!timeValue) return "";

  const date = new Date();
  date.setHours(timeValue.hour, timeValue.minute, 0, 0);

  const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
  return new Intl.DateTimeFormat(userLocale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: !is24Hour,
  }).format(date);
}

/**
 * Formats a TimeValue for HTML time input (HH:MM)
 * @param timeValue - Time value with hour and minute
 * @returns Time string in HH:MM format
 */
export function formatTimeForHtmlInput(timeValue: TimeValue): string {
  if (!timeValue) return "";
  const hour = String(timeValue.hour).padStart(2, "0");
  const minute = String(timeValue.minute).padStart(2, "0");
  return `${hour}:${minute}`;
}

/**
 * Converts a TimeValue to a Date object (using today's date)
 * @param timeValue - Time value with hour and minute
 * @param defaultHour - Default hour if timeValue is undefined
 * @param defaultMinute - Default minute if timeValue is undefined
 * @returns Date object set to the specified time
 */
export function timeValueToDate(timeValue: TimeValue | undefined, defaultHour = 9, defaultMinute = 0): Date {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  if (timeValue) {
    return new Date(year, month, day, timeValue.hour, timeValue.minute, 0, 0);
  }
  return new Date(year, month, day, defaultHour, defaultMinute, 0, 0);
}

/**
 * Converts a Date object to a TimeValue
 * @param date - Date object
 * @returns TimeValue with hour and minute
 */
export function dateToTimeValue(date: Date): TimeValue {
  return {
    hour: date.getHours(),
    minute: date.getMinutes(),
  };
}

/**
 * Parses a time string (HH:MM or HH:MM:SS) to a TimeValue
 * @param timeString - Time string in HH:MM or HH:MM:SS format
 * @returns TimeValue with hour and minute
 */
export function timeStringToTimeValue(timeString: string): TimeValue {
  const [hour, minute] = timeString.split(":").map(Number);
  return { hour: hour ?? 0, minute: minute ?? 0 };
}

/**
 * Converts a TimeValue to a time string (HH:MM)
 * @param timeValue - Time value with hour and minute
 * @returns Time string in HH:MM format
 */
export function timeValueToTimeString(timeValue: TimeValue): string {
  return `${String(timeValue.hour).padStart(2, "0")}:${String(timeValue.minute).padStart(2, "0")}`;
}

/**
 * Parses a time string (HH:MM) to a Date object
 * @param timeString - Time string in HH:MM format
 * @returns Date object set to the specified time
 */
export function timeStringToDate(timeString: string): Date {
  const [hours, minutes] = timeString.split(":").map(Number);
  if (!(Number.isInteger(hours) && Number.isInteger(minutes))) {
    throw new Error(`Invalid time string: ${timeString}`);
  }
  const d = new Date();
  d.setHours(hours!, minutes!, 0, 0);
  return d;
}

/**
 * Converts a Date object to a time string (HH:MM)
 * @param date - Date object
 * @returns Time string in HH:MM format
 */
export function dateToTimeString(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * Converts HTML time input value to TimeValue
 * @param timeString - Time string from HTML input (HH:MM)
 * @returns TimeValue with hour and minute
 */
export function htmlTimeToTimeValue(timeString: string): TimeValue {
  if (!timeString) return { hour: 0, minute: 0 };
  const [hour, minute] = timeString.split(":").map(Number);
  return { hour: hour ?? 0, minute: minute ?? 0 };
}

// ============================================================================
// Time Range Utilities
// ============================================================================

/**
 * Creates a Date object for the start of today (00:00)
 * @returns Date object set to midnight
 */
export function getStartOfDay(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Creates a Date object for the end of today (23:59)
 * @returns Date object set to 23:59
 */
export function getEndOfDay(): Date {
  const date = new Date();
  date.setHours(23, 59, 0, 0);
  return date;
}

/**
 * Rounds a date to the nearest minute interval
 * @param date - Date to round
 * @param minuteInterval - Interval in minutes (default 5)
 * @returns Rounded Date object
 */
export function roundToMinuteInterval(date: Date, minuteInterval = 5): Date {
  const newDate = new Date(date);
  newDate.setMinutes(newDate.getMinutes() - (newDate.getMinutes() % (minuteInterval || 60)), 0, 0);
  return newDate;
}

// ============================================================================
// Validation Utilities
// ============================================================================

/**
 * Validates a time string format (HH:MM or HH:MM:SS)
 * @param time - Time string to validate
 * @param label - Optional label for error message
 * @returns Object with isValid boolean and optional message
 */
export function validateTimeString(time: string, label?: string): { isValid: boolean; message?: string } {
  const message = `Provide ${label ?? "time"} in the time format HH:MM`;
  const parts = time.split(":");
  if (parts.length < 2 || parts.length > 3) return { isValid: false, message };

  for (const part of parts) {
    if (!Number.isInteger(Number(part))) return { isValid: false, message };
  }

  try {
    const date = timeStringToDate(time);
    if (Number.isNaN(date.getTime())) {
      return {
        isValid: false,
        message: `${label ?? "Time"} '${time}' is not a valid time of day.`,
      };
    }
  } catch {
    return {
      isValid: false,
      message: `${label ?? "Time"} '${time}' is not a valid time of day.`,
    };
  }

  return { isValid: true };
}

// ============================================================================
// 24-hour Time Utilities (for TimePickerWith24Hour)
// ============================================================================

const TIME_REGEX = /^(\d{1,2}):(\d{2})$/;

export const MIDNIGHT_24: TimeValue = { hour: 24, minute: 0 };
export const LAST_MINUTE: TimeValue = { hour: 23, minute: 59 };
export const MIDNIGHT_00: TimeValue = { hour: 0, minute: 0 };

interface ParseTimeResult {
  success: boolean;
  value?: TimeValue;
  error?: string;
}

/**
 * Parses a time string with support for 24:00 (midnight end of day)
 * @param timeString - Time string in HH:MM format
 * @returns Parse result with success status and value or error
 */
export function parseTimeStringWith24Hour(timeString: string): ParseTimeResult {
  const trimmed = timeString.trim();
  const match = trimmed.match(TIME_REGEX);

  if (!match) {
    return { success: false, error: "Please enter time in HH:MM format (e.g., 14:30)" };
  }

  const hour = Number.parseInt(match[1]!, 10);
  const minute = Number.parseInt(match[2]!, 10);

  if (hour < 0 || hour > 24) {
    return { success: false, error: "Hour must be between 00 and 24" };
  }

  if (hour === 24 && minute !== 0) {
    return { success: false, error: "24:00 is the only valid time with hour 24" };
  }

  if (minute < 0 || minute > 59) {
    return { success: false, error: "Minute must be between 00 and 59" };
  }

  return { success: true, value: { hour, minute } };
}

/**
 * Formats a time value for input, supporting 24:00
 * @param hour - Hour (0-24)
 * @param minute - Minute (0-59)
 * @returns Formatted time string
 */
export function formatTimeWith24Hour(hour: number, minute: number): string {
  if (hour === 24 && minute === 0) return "24:00";
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/**
 * Converts hour 24 to 0 for Date operations
 * @param hour - Hour (0-24)
 * @returns Hour suitable for Date (0-23)
 */
export function hour24ToDateHour(hour: number): number {
  return hour === 24 ? 0 : hour;
}

/**
 * Creates a Date from hour and minute, handling 24:00
 * @param hour - Hour (0-24)
 * @param minute - Minute (0-59)
 * @returns Date object
 */
export function createTimeDate(hour: number, minute: number): Date {
  const date = new Date();
  date.setHours(hour24ToDateHour(hour), minute, 0, 0);
  return date;
}

/**
 * Checks if time is 23:59 (last minute of day)
 * @param hour - Hour
 * @param minute - Minute
 * @returns True if time is 23:59
 */
export function isLastMinute(hour: number, minute: number): boolean {
  return hour === LAST_MINUTE.hour && minute === LAST_MINUTE.minute;
}
